import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { CourseWizardData } from "../types";
import { mapWizardDataToCourseInputForm } from "../payload/mapWizardDataToCourseInputForm";
import { buildCoursePayload } from "../payload/buildCoursePayload";

interface UseSubmitCourseV2Args {
  corporateId: string;
}

interface SubmitResult {
  loId: number;
  initiativesAssociated: number;
  topicsAssociated: number;
  categoriesAssociated: number;
}

interface UseSubmitCourseV2Return {
  submit: (data: CourseWizardData) => Promise<SubmitResult>;
  isSubmitting: boolean;
  error: string | null;
}

export const useSubmitCourseV2 = ({
  corporateId,
}: UseSubmitCourseV2Args): UseSubmitCourseV2Return => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const coursePostUrl = `${ServicesURL["learning-catalogue"]}/${corporateId}/learning-objects/courses`;

  const { trigger: triggerAddCourse } = useSWRMutation(
    coursePostUrl,
    (url: string, { arg }: { arg: unknown }) =>
      fetcher(url, MethodType.POST, arg),
  );

  const { trigger: triggerInitiativeAssoc } = useSWRMutation(
    `${ServicesURL.catalogDomainAssociation}/${corporateId}/learning-objects/initiatives`,
    (
      _url: string,
      { arg }: { arg: { loId: number; initiativeId: number } },
    ) => {
      const realUrl = `${ServicesURL.catalogDomainAssociation}/${corporateId}/learning-objects/${arg.loId}/initiatives`;
      return fetcher(realUrl, MethodType.PUT, { id: arg.initiativeId });
    },
  );

  const { trigger: triggerTopicAssoc } = useSWRMutation(
    ServicesURL.catalogTopicAssociation,
    (
      _url: string,
      { arg }: { arg: { loId: number; initiativeId: number; topicId: number } },
    ) => {
      const realUrl = ServicesURL.catalogTopicAssociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(arg.loId))
        .replace(":initiativeId", String(arg.initiativeId));
      return fetcher(realUrl, MethodType.PUT, { id: arg.topicId });
    },
  );

  const { trigger: triggerCategoryAssoc } = useSWRMutation(
    ServicesURL.catalogCategoryAssociation,
    (
      _url: string,
      {
        arg,
      }: { arg: { loId: number; initiativeId: number; categoryId: number } },
    ) => {
      const realUrl = ServicesURL.catalogCategoryAssociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(arg.loId))
        .replace(":initiativeId", String(arg.initiativeId));
      return fetcher(realUrl, MethodType.PUT, { id: arg.categoryId });
    },
  );

  const submit = async (data: CourseWizardData): Promise<SubmitResult> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const inputForm = mapWizardDataToCourseInputForm(data);
      const payload = buildCoursePayload(inputForm);
      const created = (await triggerAddCourse(payload)) as { id: number };
      const loId = created.id;

      let initiativesAssociated = 0;
      for (const init of data.selected_initiatives) {
        await triggerInitiativeAssoc({
          loId,
          initiativeId: Number(init.id),
        });
        initiativesAssociated += 1;
      }

      let topicsAssociated = 0;
      let categoriesAssociated = 0;
      for (const init of data.selected_initiatives) {
        const initiativeId = Number(init.id);
        const editorial = data.editorial_per_initiative[initiativeId] ?? {
          selected_carousel_ids: [],
          selected_topic_ids: [],
          selected_skill_ids: [],
        };

        for (const topicId of editorial.selected_topic_ids) {
          await triggerTopicAssoc({ loId, initiativeId, topicId });
          topicsAssociated += 1;
        }

        for (const categoryId of editorial.selected_carousel_ids) {
          await triggerCategoryAssoc({ loId, initiativeId, categoryId });
          categoriesAssociated += 1;
        }

        // TODO: skill BE non pronto. Quando esposto, aggiungere qui un
        // loop su `editorial.selected_skill_ids` come per topics/categorie.
      }

      return {
        loId,
        initiativesAssociated,
        topicsAssociated,
        categoriesAssociated,
      };
    } catch (e: any) {
      const msg = e?.message || "Submit corso fallito";
      setError(msg);
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting, error };
};
